import { useCallback, useEffect, useMemo } from 'react';
import { IrisGridUtils } from '@deephaven/iris-grid';
import { useLayoutManager, useDashboardId, useAppSelector, useDhId } from '@deephaven/dashboard';
import { getInputFiltersForDashboard } from "./redux/index.js";
import { emitFilterColumnsChanged, emitFilterTableChanged } from "./FilterEvents.js";
/**
 * Subscribes to the dashboard column filters (a.k.a. InputFilter) for the current panel or widget, and
 * adds the columns provided to the filter options in the dashboard.
 * @param columns The columns this source has available for filtering.
 *                These are used to populate filter options in the UI (InputFilter, DropdownFilter).
 *                null can be used to indicate the source is not yet ready which is useful
 *                to preserve
 * @param table The table for this source if applicable.
 *              This is used to enable ChartBuilder from IrisGrid.
 * @returns The dashboard column filters (InputFilter[]) that apply to the columns provided.
 */
export function useDashboardColumnFilters(columns, table) {
  var _useLayoutManager = useLayoutManager(),
    eventHub = _useLayoutManager.eventHub;
  var dashboardId = useDashboardId();
  var panelId = useDhId();
  useEffect(function columnsChanged() {
    if (panelId == null || columns == null) {
      return;
    }
    emitFilterColumnsChanged(eventHub, panelId, columns);
  }, [eventHub, panelId, columns]);
  useEffect(function tableChanged() {
    if (table == null || panelId == null) {
      return;
    }
    emitFilterTableChanged(eventHub, panelId, table);
  }, [eventHub, panelId, table]);

  // Cleanup separately because filtering the table or other operations can get a new columns array,
  // and we are using null to indicate unmount, not change
  useEffect(function cleanupOnUnmount() {
    if (panelId == null) {
      return;
    }
    return () => {
      emitFilterColumnsChanged(eventHub, panelId, null);
      emitFilterTableChanged(eventHub, panelId, null);
    };
  }, [eventHub, panelId]);
  var getInputFilters = useCallback(s => getInputFiltersForDashboard(s, dashboardId), [dashboardId]);
  var reduxInputFilters = useAppSelector(getInputFilters);
  var inputFilters = useMemo(() => IrisGridUtils.getInputFiltersForColumns(columns !== null && columns !== void 0 ? columns : [],
  // They may have picked a column, but not actually entered a value yet. In that case, don't need to update.
  reduxInputFilters.filter(_ref => {
    var value = _ref.value,
      excludePanelIds = _ref.excludePanelIds;
    return value != null && (excludePanelIds == null || panelId != null && !excludePanelIds.includes(panelId));
  })), [columns, panelId, reduxInputFilters]);
  return inputFilters;
}
export default useDashboardColumnFilters;
//# sourceMappingURL=useDashboardColumnFilters.js.map